import { useCallback, useMemo, useState, useEffect } from 'react';
import { useAppSelector, useAppDispatch } from 'MeetingsLib/redux/reducers';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { getIsValidatingStagedEmail, getStagedEmailValidationResults } from 'MeetingsLib/selectors/FormFieldValidationSelector';
import I18n from 'I18n';
import debounce from 'transmute/debounce';
// @ts-ignore ts-migrate(7016) FIXME: Could not find a declaration file for module 'Patt... Remove this comment to see the full error message
import EmailAddressPattern from 'PatternValidationJS/patterns/EmailAddress';
import { useEmailValidationV2 } from 'MeetingsLib/hooks/useEmailValidation';
import { getSlug, getEmbedded } from 'MeetingsLib/selectors/RESTParamSelectors';
import * as ActionTypes from 'MeetingsLib/redux/actions/ActionTypes';
import { track } from 'MeetingsLib/tracking/UsageTracker';
import { TextInput } from 'foundations-components/transitional/components/TextInput';
import { jsx as _jsx } from "react/jsx-runtime";
const buildInvalidReason = emailValidationResults => {
  const {
    invalidReason
  } = emailValidationResults;
  if (invalidReason === 'INVALID_EMAIL') {
    return I18n.text('prebook.details.errorHelper.email');
  } else if (invalidReason === 'BLOCKED_DOMAIN') {
    return I18n.text('prebook.details.errorHelper.blockedDomain', {
      domain: emailValidationResults.domain
    });
  }
  return null;
};
const FIELD_NAME = 'email';
export default function EmailFormFieldAsync({
  fieldLabel,
  fieldValue,
  autocomplete,
  placeholder = '',
  disabled,
  isRequired,
  validation,
  setFormValue
}) {
  const dispatch = useAppDispatch();
  const [isBlurred, setIsBlurred] = useState(false);
  const [emailToValidate, setEmailToValidate] = useState(fieldValue);
  const slug = useAppSelector(getSlug);
  const isEmbedded = useAppSelector(getEmbedded);
  const meetingSource = isEmbedded ? 'embedded' : 'public';
  const debouncedSetEmailToValidate = useMemo(() => debounce(500, nextVal => {
    setEmailToValidate(nextVal);
  }), []);
  useEffect(() => {
    if (EmailAddressPattern.test(fieldValue)) {
      debouncedSetEmailToValidate(fieldValue);
    }
  }, [fieldValue, debouncedSetEmailToValidate]);
  const {
    results,
    error,
    loading
  } = useEmailValidationV2({
    email: emailToValidate,
    slug
  });
  useEffect(() => {
    if (results && results.invalidReason) {
      track('Meetings', 'Meeting Event Interaction', {
        source: meetingSource,
        action: results.invalidReason === 'BLOCKED_DOMAIN' ? 'blocked free or specific domain' : 'blocked invalid domain'
      });
    }
  }, [results, meetingSource]);
  useEffect(() => {
    if (loading) {
      dispatch({
        type: ActionTypes.ASYNC_STAGED_EMAIL_VALIDATION_STARTED
      });
    }
    if (results) {
      dispatch({
        type: ActionTypes.ASYNC_STAGED_EMAIL_VALIDATION_COMPLETED,
        payload: results
      });
    }
    if (error) {
      dispatch({
        type: ActionTypes.ASYNC_STAGED_EMAIL_VALIDATION_FAILED,
        payload: error
      });
    }
  }, [dispatch, error, loading, results]);

  /**
   * We are reading from the redux store here for consistency with the global selectors
   * Even though we could just read from the hook directly, we are reading here to keep the
   * logic consistent with the rest of the codebase
   */
  const isValidatingEmail = useAppSelector(getIsValidatingStagedEmail);
  const emailValidationResults = useAppSelector(getStagedEmailValidationResults);
  const handleInputChange = useCallback(e => {
    setFormValue(FIELD_NAME, e.target.value.trim());
  }, [setFormValue]);
  const onFieldBlur = useCallback(() => {
    setIsBlurred(true);
  }, []);
  const validationMessage = useMemo(() => {
    if (isValidatingEmail) {
      return I18n.text('prebook.details.errorHelper.validatingEmail');
    }
    if (validation) {
      return validation;
    }
    if (emailValidationResults && !emailValidationResults.isValidEmail) {
      return buildInvalidReason(emailValidationResults);
    }
    return null;
  }, [isValidatingEmail, emailValidationResults, validation]);
  const hasError = useMemo(() => {
    if (!fieldValue && !isBlurred || isValidatingEmail) {
      return false;
    }
    return Boolean(validationMessage);
  }, [fieldValue, isBlurred, isValidatingEmail, validationMessage]);
  const showValidationMessage = useMemo(() => {
    return Boolean(isBlurred || fieldValue);
  }, [isBlurred, fieldValue]);
  return /*#__PURE__*/_jsx(FormControl, {
    "data-selenium-test": "booking-form-field-email",
    "data-test-id": "booking-form-field-email",
    label: fieldLabel,
    required: isRequired,
    error: hasError,
    validationMessage: showValidationMessage && /*#__PURE__*/_jsx("span", {
      "data-test-id": "booking-form-field-email-validation-message",
      children: validationMessage
    }),
    onBlur: onFieldBlur,
    marginBlockStart: "600",
    children: /*#__PURE__*/_jsx(TextInput, {
      dir: "auto",
      maxInlineSize: "100%",
      textAlign: "start",
      autoComplete: autocomplete,
      type: "text",
      name: FIELD_NAME,
      "data-test-id": "booking-form-field-email-input",
      value: fieldValue || '',
      onChange: handleInputChange,
      onBlur: onFieldBlur,
      placeholder: placeholder,
      disabled: disabled
    })
  });
}